'use client';

import { c as _c } from "react/compiler-runtime";
import { type ItemsRenderProps, useGetMarketsInfinite, useMarketConfigCatalog } from '@morfi/backend-sdk-v2/client';
import { usePathname, useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import type { Dispatch, SetStateAction } from 'react';
import { useEffect, useRef, useState } from 'react';
import { marketTypeFilterParams, parseMarketTypeParam, useMarketTypeFilter } from '@/components/market/identity/market-identity-filter';
import { MarketFeedTradeProvider } from '@/components/markets/live-trades/market-feed-trade-provider';
import { MarketRowCardSkeleton } from '@/components/markets/market-row-card-skeleton';
import { type FilterDimension, type FilterValue, SortFilterBar } from '@/components/shared/sort-filter-bar';
import type { SortPillOption } from '@/components/shared/sort-pill-row';
import type { ChipOption } from '@/components/ui/chip-multi-select';
import { Icon } from '@/components/ui/icon';
import { Award01IconSolid, CheckmarkCircle02IconSolid, Clock04IconSolid, FireIconSolid, Loading03Icon, Plant04IconSolid } from '@/components/ui/icon/glyphs';
import type { MultiSelectOption } from '@/components/ui/searchable-multi-select';
import { categoryNameToId, DISCOVERY_CATEGORIES, locationNameToId, PROPOSAL_LOCATIONS } from '@/config/proposal-options';
import { onEvent } from '@/events';
import { useMediaQuery } from '@/hooks/ui/use-media-query';
import { useAuthGate } from '@/hooks/use-auth-gate';
import { useAuthenticatedWatchlist } from '@/hooks/use-authenticated-watchlist';
import { useFeatureGate } from '@/hooks/use-feature-gate';
import { useRoutePerf } from '@/hooks/use-route-perf';
import { useFormattedCurrency } from '@/lib/currency';
import { creatorParam, csvParam, dateRangeParams, dateRangeToParam, dollarsToD6, intString, type OutcomesBounds, paramToCsv, paramToDateRange, paramToRange, type Range, rangeParams, rangeToParam } from '@/lib/filters/feed-filters';
import { MARKETS_LIST_OPEN_STATUS, MARKETS_LIST_RESOLVED_STATUS } from '@/lib/market/listing-status';
import { LISTING_INDEX_PAGE_STACK_CLASS } from '@/lib/ui/layout';
import { cn } from '@/lib/ui/utils';
import { useMarketsSortStore } from '@/stores/markets-sort';
import { EmptyState } from './empty-state';
import { type MarketsEmptyStateKind, resolveMarketsEmptyVariant } from './empty-state-variant';
import { MarketsFeaturedSection } from './featured-market-hero/markets-featured-section';
import { IntroWidget } from './intro-widget/intro-widget';
import { MarketRowCard } from './market-row-card';
import { MarketsDiscoveryActions } from './markets-discovery-actions';
import { MarketsFeedError } from './markets-feed-error';
import { type MarketsFeedPhase, resolveMarketsFeedPhase } from './markets-feed-phase';
import { getMarketsFeedSortOrder, MARKETS_FEED_DEFAULT_SORT, MARKETS_FEED_SORT_KEYS } from './markets-feed-sorts';
import { MarketsHeader } from './markets-header';
import { NoResults } from './no-results';
import { SparseCard } from './sparse-card';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const SEARCH_DEBOUNCE_MS = 300;

// Stable keys for the in-page filter-change skeleton stripe. Three rows
// covers the most-common viewport widths (1/2/3 columns) without
// overflowing the visible region — the real grid lands within ~hundreds
// of ms once the new query settles, so a long stripe would mostly just
// flash.
const SKELETON_STRIPE_KEYS: readonly string[] = ['stripe-0', 'stripe-1', 'stripe-2'];
const DEFAULT_STATUS = 'trading';
const VALID_STATUSES: Set<string> = new Set(['trading', 'resolved']);

/**
 * Unsatisfiable-but-valid `pubkeys` filter for the empty-watchlist case. The
 * all-1s base58 string is the Solana system/default pubkey (32 zero bytes); it
 * decodes cleanly (unlike a `__none__`-style sentinel, which throws in the
 * backend's strict base58 decoder) and matches no real market, so the feed
 * stays empty instead of the backend's missing-filter branch returning every
 * market (MOR-386).
 */
const WATCHLIST_EMPTY_SENTINEL = '11111111111111111111111111111111';

/**
 * Pseudo-sort value used to render the "Winners" pill in the sort tab row.
 * It's not a real sort — clicking it switches the status filter to 'resolved'
 * (which the Winners feed reads as "show graduated markets") while leaving the
 * underlying sortBy untouched, so toggling back to a real sort restores the
 * user's previous sort selection.
 */
const RESOLVED_VIEW_VALUE = 'resolved-view';
const VALID_SORT_VALUES: Set<string> = new Set(MARKETS_FEED_SORT_KEYS);
const DEFAULT_SORT: string = MARKETS_FEED_DEFAULT_SORT;
const VALID_LOCATIONS: Set<string> = new Set(PROPOSAL_LOCATIONS.map(l => l.name));

/**
 * MOR-1800: the `?category=` param must apply regardless of casing or
 * id-vs-name form (`technology`, `Technology`, `TECH…`). Resolves to the
 * canonical display name when the value matches a known category id or name;
 * otherwise keeps the raw value so the backend's exact-match filter still
 * applies (live data carries legacy non-canonical slugs like `tech`).
 */
function resolveCategoryParam(value: string): string | null {
  const v = value.trim();
  if (v.length === 0) return null;
  const match = DISCOVERY_CATEGORIES.find(c => c.id.toLowerCase() === v.toLowerCase() || c.name.toLowerCase() === v.toLowerCase());
  return match?.name ?? v;
}
const REGION_MULTI_OPTIONS: MultiSelectOption[] = PROPOSAL_LOCATIONS.map(l => ({
  value: l.name,
  label: l.name,
  emoji: l.emoji
}));
const CATEGORY_CHIP_OPTIONS: ChipOption[] = DISCOVERY_CATEGORIES.map(c => ({
  value: c.name,
  label: c.name,
  emoji: c.emoji
}));

/** Read a FilterValue as a string[] (chipsMulti), defaulting to empty. */
function asStrArr(value: FilterValue | undefined): string[] {
  if (Array.isArray(value) && (value.length === 0 || typeof value[0] === 'string')) {
    return value as string[];
  }
  return [];
}

// Pre-resolution fallbacks for the outcomes bounds. The real values come from
// PlatformConfig.{minOutcomes,maxOutcomes}; these only apply before the config
// query resolves or if it fails.
const FALLBACK_MIN_OUTCOMES = 2;
const FALLBACK_MAX_OUTCOMES = 12;
// Slider/date bounds — mirror the filter-overhaul mockup (`/tmp/morfi-filters`).
// `outcomes` here is only the fallback span; the live bounds are sourced from
// PlatformConfig and threaded as `outcomesBounds`.
const M = {
  outcomes: {
    min: FALLBACK_MIN_OUTCOMES,
    max: FALLBACK_MAX_OUTCOMES
  },
  volume: {
    min: 0,
    max: 100_000
  },
  prizePool: {
    min: 0,
    max: 50_000
  },
  liquidity: {
    min: 0,
    max: 250_000
  },
  backers: {
    min: 0,
    max: 500
  },
  traders: {
    min: 0,
    max: 1000
  }
} as const;
const MARKETS_CREATED_SPAN_DAYS = 180;

/** Read a FilterValue as a MultiSelectOption[] (searchMulti / creatorMulti). */
function asOptArr(value: FilterValue | undefined): MultiSelectOption[] {
  if (Array.isArray(value) && (value.length === 0 || typeof value[0] === 'object')) {
    return value as MultiSelectOption[];
  }
  return [];
}

/** Read a FilterValue as a `[lo, hi]` range, or `undefined` when not a range. */
function asRangeVal(value: FilterValue | undefined): Range | undefined {
  if (Array.isArray(value) && value.length === 2 && typeof value[0] === 'number') {
    return [value[0], value[1] as number];
  }
  return undefined;
}
const MARKETS_FILTER_TYPE_BY_KEY: Record<string, 'category' | 'location' | 'outcomes' | 'volume' | 'prizePool' | 'liquidity' | 'backers' | 'traders' | 'createdAt' | 'creator'> = {
  category: 'category',
  location: 'location',
  outcomes: 'outcomes',
  volume: 'volume',
  prizePool: 'prizePool',
  liquidity: 'liquidity',
  backers: 'backers',
  traders: 'traders',
  createdAt: 'createdAt',
  creator: 'creator'
};

/** A compact analytics-safe summary of any FilterValue. */
function summariseFilterValue(value: FilterValue | undefined): string {
  if (value === undefined || value === null) return 'all';
  if (typeof value === 'string') return value.length > 0 ? value : 'all';
  if (Array.isArray(value)) {
    if (value.length === 0) return 'all';
    if (typeof value[0] === 'number') return `${value[0]}-${value[1] as number}`;
    if (typeof value[0] === 'string') return (value as string[]).join(',');
    return (value as MultiSelectOption[]).map(o => o.value).join(',');
  }
  return 'all';
}

/** A fresh, fully-cleared markets overhaul value map. */
function buildEmptyMarketsOverhaul(outcomesBounds: OutcomesBounds): Record<string, FilterValue | undefined> {
  return {
    location: [],
    outcomes: [outcomesBounds.min, outcomesBounds.max],
    volume: [M.volume.min, M.volume.max],
    prizePool: [M.prizePool.min, M.prizePool.max],
    liquidity: [M.liquidity.min, M.liquidity.max],
    backers: [M.backers.min, M.backers.max],
    traders: [M.traders.min, M.traders.max],
    createdAt: [0, 100],
    creator: []
  };
}

/** Serialise the markets overhaul values to a shareable URL param map. */
function buildMarketsUrlParams(overhaul: Record<string, FilterValue | undefined>, outcomesBounds: OutcomesBounds, regionEnabled: boolean, identityEnabled: boolean): Record<string, string> {
  // Category is intentionally NOT serialised here — the global top category nav
  // owns the `?category=` param. The overhaul URL key carries only modal filters.
  const out: Record<string, string> = marketTypeFilterParams(overhaul.marketType, identityEnabled);
  if (regionEnabled) {
    const regions = asOptArr(overhaul.location).map(o => o.value);
    if (regions.length > 0) out.location = regions.join(',');
  }
  const addRange = (key: string, range: Range | undefined, min: number, max: number): void => {
    if (range === undefined) return;
    const v = rangeToParam(range, min, max);
    if (v !== null) out[key] = v;
  };
  addRange('outcomes', asRangeVal(overhaul.outcomes), outcomesBounds.min, outcomesBounds.max);
  addRange('volume', asRangeVal(overhaul.volume), M.volume.min, M.volume.max);
  addRange('prizePool', asRangeVal(overhaul.prizePool), M.prizePool.min, M.prizePool.max);
  addRange('liquidity', asRangeVal(overhaul.liquidity), M.liquidity.min, M.liquidity.max);
  addRange('backers', asRangeVal(overhaul.backers), M.backers.min, M.backers.max);
  addRange('traders', asRangeVal(overhaul.traders), M.traders.min, M.traders.max);
  const created = asRangeVal(overhaul.createdAt);
  if (created !== undefined) {
    const v = dateRangeToParam(created);
    if (v !== null) out.createdAt = v;
  }
  return out;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Expand the virtual `trading`/`resolved` UI toggle to `/v1/markets` status tokens. */
function resolveStatusArg(status: string): string | undefined {
  if (status === 'resolved') return MARKETS_LIST_RESOLVED_STATUS;
  if (status === 'trading') return MARKETS_LIST_OPEN_STATUS;
  if (status === 'all') return undefined;
  return status;
}

/** Build a query string from non-default filter/sort values. */
function buildSearchString(sort: string, status: string, search: string, currentParams: URLSearchParams, overhaulUrlKey: string, watchlistActive: boolean): string {
  const params = new URLSearchParams();
  if (sort !== DEFAULT_SORT) params.set('sort', sort);
  // Category is owned by the global top nav (`CategoryNav`) in BOTH modes — it
  // drives the feed + featured-hero visibility. Preserve it STRAIGHT FROM THE
  // URL (not the one-render-lagged `category` state) so this sync never fights
  // the category→state effect and never erases the nav selection.
  const cat = currentParams.get('category');
  if (cat !== null) params.set('category', cat);
  // The overhaul owns the gated location filter and range filters — emit from
  // its serialised query string.
  for (const [k, v] of new URLSearchParams(overhaulUrlKey)) params.set(k, v);
  if (status !== DEFAULT_STATUS) params.set('status', status);
  if (search.length > 0) params.set('q', search);
  if (watchlistActive) params.set('view', 'watchlist');
  const qs = params.toString();
  return qs.length > 0 ? `?${qs}` : '';
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface MarketsPageModel {
  t: ReturnType<typeof useTranslations>;
  tFilters: ReturnType<typeof useTranslations>;
  category: string | null;
  status: string;
  sortBy: string;
  showFeaturedHero: boolean;
  marketWatchlistPubkeys: readonly string[];
  feedPhase: MarketsFeedPhase;
  retryFeed: () => void;
  listIsLoading: boolean;
  totalMarkets: number;
  emptyStateVariant: MarketsEmptyStateKind;
  showCreateCta: boolean;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  ids: readonly string[];
  Items: ReturnType<typeof useGetMarketsInfinite>['Items'];
  /** First-seen index per market id — drives above-the-fold `priorityImage`. */
  marketRowIndexById: Map<string, number>;
  sortOptions: SortPillOption[];
  activeSortPill: string;
  handleSortPillChange: (next: string) => void;
  handleSortPillEvent: (value: string, previous: string) => void;
  filterDimensions: FilterDimension[];
  activeFilterValues: Record<string, FilterValue | undefined>;
  handleFilterChange: (key: string, value: FilterValue) => void;
  handleClearFilters: () => void;
  filterOpen: boolean;
  setFilterOpen: Dispatch<SetStateAction<boolean>>;
  headerActions: React.ReactNode;
}

/** Own feed membership, URL filters and page controls; live rows belong to child subscribers. */
function useMarketsPage() {
  const $ = _c(323);
  const t = useTranslations("markets");
  const tFilters = useTranslations("filters");
  const regionEnabled = useFeatureGate("region") === true;
  const identityEnabled = useFeatureGate("creator_and_platform_markets") === true;
  const marketTypeFilter = useMarketTypeFilter(identityEnabled);
  const {
    exchangeRate,
    currencySymbol
  } = useFormattedCurrency();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const initialSort = searchParams.get("sort");
  const initialCategory = searchParams.get("category");
  const initialStatus = searchParams.get("status");
  const [sortBy, setSortBy] = useState(initialSort !== null && VALID_SORT_VALUES.has(initialSort) ? initialSort : DEFAULT_SORT);
  const [status, setStatus] = useState(initialStatus !== null && VALID_STATUSES.has(initialStatus) ? initialStatus : DEFAULT_STATUS);
  const [category, setCategory] = useState(initialCategory !== null ? resolveCategoryParam(initialCategory) : null);
  let t0;
  if ($[0] !== searchParams) {
    t0 = () => searchParams.get("q") ?? "";
    $[0] = searchParams;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const [search] = useState(t0);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const catalog = useMarketConfigCatalog();
  let t1;
  if ($[2] !== catalog.configs) {
    t1 = Object.values(catalog.configs ?? {});
    $[2] = catalog.configs;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const configs = t1;
  let t2;
  if ($[4] !== configs) {
    t2 = configs.length === 0 ? FALLBACK_MIN_OUTCOMES : Math.min(...configs.map(_temp));
    $[4] = configs;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== configs) {
    t3 = configs.length === 0 ? FALLBACK_MAX_OUTCOMES : Math.max(...configs.map(_temp2));
    $[6] = configs;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t2 || $[9] !== t3) {
    t4 = {
      min: t2,
      max: t3
    };
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const outcomesBounds = t4;
  let t5;
  if ($[11] !== outcomesBounds.max || $[12] !== outcomesBounds.min || $[13] !== searchParams) {
    t5 = () => ({
      marketType: parseMarketTypeParam(searchParams.get("marketType")),
      location: REGION_MULTI_OPTIONS.filter(o => paramToCsv(searchParams.get("location"), VALID_LOCATIONS).includes(o.value)),
      outcomes: paramToRange(searchParams.get("outcomes"), outcomesBounds.min, outcomesBounds.max),
      volume: paramToRange(searchParams.get("volume"), M.volume.min, M.volume.max),
      prizePool: paramToRange(searchParams.get("prizePool"), M.prizePool.min, M.prizePool.max),
      liquidity: paramToRange(searchParams.get("liquidity"), M.liquidity.min, M.liquidity.max),
      backers: paramToRange(searchParams.get("backers"), M.backers.min, M.backers.max),
      traders: paramToRange(searchParams.get("traders"), M.traders.min, M.traders.max),
      createdAt: paramToDateRange(searchParams.get("createdAt")),
      creator: []
    });
    $[11] = outcomesBounds.max;
    $[12] = outcomesBounds.min;
    $[13] = searchParams;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const [overhaul, setOverhaul] = useState(t5);
  let t6;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = function setOverhaulValue(key, value) {
      setOverhaul(prev => ({
        ...prev,
        [key]: value
      }));
    };
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const setOverhaulValue = t6;
  const debounceTimerRef = useRef(null);
  let t7;
  if ($[16] !== searchParams) {
    t7 = searchParams.get("category");
    $[16] = searchParams;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const categoryParam = t7;
  let t8;
  let t9;
  if ($[18] !== categoryParam) {
    t8 = () => {
      setCategory(categoryParam !== null ? resolveCategoryParam(categoryParam) : null);
    };
    t9 = [categoryParam];
    $[18] = categoryParam;
    $[19] = t8;
    $[20] = t9;
  } else {
    t8 = $[19];
    t9 = $[20];
  }
  useEffect(t8, t9);
  let t10;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      min: FALLBACK_MIN_OUTCOMES,
      max: FALLBACK_MAX_OUTCOMES
    };
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  const prevOutcomesBoundsRef = useRef(t10);
  let t11;
  let t12;
  if ($[22] !== outcomesBounds.max || $[23] !== outcomesBounds.min) {
    t11 = () => {
      const prev_0 = prevOutcomesBoundsRef.current;
      if (prev_0.min === outcomesBounds.min && prev_0.max === outcomesBounds.max) {
        return;
      }
      prevOutcomesBoundsRef.current = {
        min: outcomesBounds.min,
        max: outcomesBounds.max
      };
      setOverhaul(cur => {
        const range = asRangeVal(cur.outcomes);
        if (range !== undefined && range[0] === prev_0.min && range[1] === prev_0.max) {
          return {
            ...cur,
            outcomes: [outcomesBounds.min, outcomesBounds.max]
          };
        }
        return cur;
      });
    };
    t12 = [outcomesBounds.min, outcomesBounds.max];
    $[22] = outcomesBounds.max;
    $[23] = outcomesBounds.min;
    $[24] = t11;
    $[25] = t12;
  } else {
    t11 = $[24];
    t12 = $[25];
  }
  useEffect(t11, t12);
  const categorySelected = categoryParam !== null ? categoryParam.split(",").map(resolveCategoryParam).filter(_temp3) : [];
  let t13;
  if ($[26] !== pathname || $[27] !== searchParams) {
    t13 = function applyCategoryToUrl(values) {
      const params = new URLSearchParams(searchParams.toString());
      if (values.length > 0) {
        params.set("category", values.join(","));
      } else {
        params.delete("category");
      }
      const qs = params.toString();
      const nextUrl = `${pathname}${qs.length > 0 ? `?${qs}` : ""}`;
      window.history.replaceState(null, "", nextUrl);
    };
    $[26] = pathname;
    $[27] = searchParams;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  const applyCategoryToUrl = t13;
  let t14;
  if ($[29] !== searchParams) {
    t14 = () => searchParams.get("view") === "watchlist";
    $[29] = searchParams;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  const [watchlistActive, setWatchlistActive] = useState(t14);
  let t15;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = {
      kind: "markets",
      fields: ["pubkeys"]
    };
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  const {
    data: marketsWatchlist,
    isPending: marketsWatchlistPending
  } = useAuthenticatedWatchlist(t15);
  let t16;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = {
      kind: "winners",
      fields: ["pubkeys"]
    };
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  const t17 = status === "resolved";
  let t18;
  if ($[33] !== t17) {
    t18 = {
      enabled: t17
    };
    $[33] = t17;
    $[34] = t18;
  } else {
    t18 = $[34];
  }
  const {
    data: winnersWatchlist,
    isPending: winnersWatchlistPending
  } = useAuthenticatedWatchlist(t16, t18);
  const watchlistPending = status === "resolved" ? marketsWatchlistPending || winnersWatchlistPending : marketsWatchlistPending;
  const watchlistLoading = watchlistActive && watchlistPending;
  const [filterOpen, setFilterOpen] = useState(false);
  const {
    ensureAuthenticated
  } = useAuthGate();
  let t19;
  let t20;
  if ($[35] !== search) {
    t19 = () => {
      if (debounceTimerRef.current !== null) {
        clearTimeout(debounceTimerRef.current);
      }
      debounceTimerRef.current = setTimeout(() => {
        debounceTimerRef.current = null;
        setDebouncedSearch(search);
      }, SEARCH_DEBOUNCE_MS);
      return () => {
        if (debounceTimerRef.current !== null) {
          clearTimeout(debounceTimerRef.current);
        }
      };
    };
    t20 = [search];
    $[35] = search;
    $[36] = t19;
    $[37] = t20;
  } else {
    t19 = $[36];
    t20 = $[37];
  }
  useEffect(t19, t20);
  let t21;
  if ($[38] !== identityEnabled || $[39] !== outcomesBounds || $[40] !== overhaul || $[41] !== regionEnabled) {
    t21 = new URLSearchParams(buildMarketsUrlParams(overhaul, outcomesBounds, regionEnabled, identityEnabled)).toString();
    $[38] = identityEnabled;
    $[39] = outcomesBounds;
    $[40] = overhaul;
    $[41] = regionEnabled;
    $[42] = t21;
  } else {
    t21 = $[42];
  }
  const overhaulUrlKey = t21;
  let t22;
  let t23;
  if ($[43] !== debouncedSearch || $[44] !== overhaulUrlKey || $[45] !== pathname || $[46] !== searchParams || $[47] !== sortBy || $[48] !== status || $[49] !== watchlistActive) {
    t22 = () => {
      const qs_0 = buildSearchString(sortBy, status, debouncedSearch, searchParams, overhaulUrlKey, watchlistActive);
      const target = `${pathname}${qs_0}`;
      const current = `${pathname}${searchParams.toString().length > 0 ? `?${searchParams.toString()}` : ""}`;
      if (target !== current) {
        window.history.replaceState(null, "", target);
      }
    };
    t23 = [sortBy, status, debouncedSearch, pathname, searchParams, overhaulUrlKey, watchlistActive];
    $[43] = debouncedSearch;
    $[44] = overhaulUrlKey;
    $[45] = pathname;
    $[46] = searchParams;
    $[47] = sortBy;
    $[48] = status;
    $[49] = watchlistActive;
    $[50] = t22;
    $[51] = t23;
  } else {
    t22 = $[50];
    t23 = $[51];
  }
  useEffect(t22, t23);
  let t24;
  if ($[52] !== status) {
    t24 = resolveStatusArg(status);
    $[52] = status;
    $[53] = t24;
  } else {
    t24 = $[53];
  }
  const statusArg = t24;
  const effectiveSortBy = status === "resolved" ? "resolvedAt" : sortBy;
  let t25;
  if ($[54] !== marketsWatchlist?.pubkeys || $[55] !== status || $[56] !== winnersWatchlist?.pubkeys) {
    t25 = status === "resolved" ? Array.from(new Set([...(marketsWatchlist?.pubkeys ?? []), ...(winnersWatchlist?.pubkeys ?? [])])) : marketsWatchlist?.pubkeys ?? [];
    $[54] = marketsWatchlist?.pubkeys;
    $[55] = status;
    $[56] = winnersWatchlist?.pubkeys;
    $[57] = t25;
  } else {
    t25 = $[57];
  }
  const watchlistPubkeys = t25;
  let t26;
  if ($[58] !== watchlistActive || $[59] !== watchlistPubkeys) {
    t26 = watchlistActive && watchlistPubkeys.length === 0 ? {
      pubkeys: WATCHLIST_EMPTY_SENTINEL
    } : watchlistActive ? {
      pubkeys: watchlistPubkeys.join(",")
    } : {};
    $[58] = watchlistActive;
    $[59] = watchlistPubkeys;
    $[60] = t26;
  } else {
    t26 = $[60];
  }
  const watchlistFilter = t26;
  const [nowMs] = useState(_temp4);
  let t27;
  if ($[61] !== identityEnabled || $[62] !== overhaul.marketType) {
    t27 = marketTypeFilterParams(overhaul.marketType, identityEnabled);
    $[61] = identityEnabled;
    $[62] = overhaul.marketType;
    $[63] = t27;
  } else {
    t27 = $[63];
  }
  let t28;
  if ($[64] !== overhaul.location || $[65] !== regionEnabled) {
    t28 = regionEnabled ? csvParam("location", asOptArr(overhaul.location).map(_temp5)) : {};
    $[64] = overhaul.location;
    $[65] = regionEnabled;
    $[66] = t28;
  } else {
    t28 = $[66];
  }
  let t29;
  if ($[67] !== outcomesBounds.max || $[68] !== outcomesBounds.min || $[69] !== overhaul.outcomes) {
    t29 = rangeParams(asRangeVal(overhaul.outcomes), outcomesBounds.min, outcomesBounds.max, "outcomesMin", "outcomesMax", intString);
    $[67] = outcomesBounds.max;
    $[68] = outcomesBounds.min;
    $[69] = overhaul.outcomes;
    $[70] = t29;
  } else {
    t29 = $[70];
  }
  let t30;
  if ($[71] !== overhaul.volume) {
    t30 = rangeParams(asRangeVal(overhaul.volume), M.volume.min, M.volume.max, "volumeMin", "volumeMax", dollarsToD6);
    $[71] = overhaul.volume;
    $[72] = t30;
  } else {
    t30 = $[72];
  }
  let t31;
  if ($[73] !== overhaul.prizePool) {
    t31 = rangeParams(asRangeVal(overhaul.prizePool), M.prizePool.min, M.prizePool.max, "prizePoolMin", "prizePoolMax", dollarsToD6);
    $[73] = overhaul.prizePool;
    $[74] = t31;
  } else {
    t31 = $[74];
  }
  let t32;
  if ($[75] !== overhaul.liquidity) {
    t32 = rangeParams(asRangeVal(overhaul.liquidity), M.liquidity.min, M.liquidity.max, "liquidityMin", "liquidityMax", dollarsToD6);
    $[75] = overhaul.liquidity;
    $[76] = t32;
  } else {
    t32 = $[76];
  }
  let t33;
  if ($[77] !== overhaul.backers) {
    t33 = rangeParams(asRangeVal(overhaul.backers), M.backers.min, M.backers.max, "backersMin", "backersMax", intString);
    $[77] = overhaul.backers;
    $[78] = t33;
  } else {
    t33 = $[78];
  }
  let t34;
  if ($[79] !== overhaul.traders) {
    t34 = rangeParams(asRangeVal(overhaul.traders), M.traders.min, M.traders.max, "tradersMin", "tradersMax", intString);
    $[79] = overhaul.traders;
    $[80] = t34;
  } else {
    t34 = $[80];
  }
  let t35;
  if ($[81] !== nowMs || $[82] !== overhaul.createdAt) {
    t35 = dateRangeParams(asRangeVal(overhaul.createdAt), MARKETS_CREATED_SPAN_DAYS, nowMs, "createdAfter", "createdBefore");
    $[81] = nowMs;
    $[82] = overhaul.createdAt;
    $[83] = t35;
  } else {
    t35 = $[83];
  }
  let t36;
  if ($[84] !== overhaul.creator) {
    t36 = creatorParam("creator", asOptArr(overhaul.creator));
    $[84] = overhaul.creator;
    $[85] = t36;
  } else {
    t36 = $[85];
  }
  let t37;
  if ($[86] !== t27 || $[87] !== t28 || $[88] !== t29 || $[89] !== t30 || $[90] !== t31 || $[91] !== t32 || $[92] !== t33 || $[93] !== t34 || $[94] !== t35 || $[95] !== t36) {
    t37 = {
      ...t27,
      ...t28,
      ...t29,
      ...t30,
      ...t31,
      ...t32,
      ...t33,
      ...t34,
      ...t35,
      ...t36
    };
    $[86] = t27;
    $[87] = t28;
    $[88] = t29;
    $[89] = t30;
    $[90] = t31;
    $[91] = t32;
    $[92] = t33;
    $[93] = t34;
    $[94] = t35;
    $[95] = t36;
    $[96] = t37;
  } else {
    t37 = $[96];
  }
  const overhaulArgs = t37;
  let t38;
  if ($[97] !== effectiveSortBy) {
    t38 = getMarketsFeedSortOrder(effectiveSortBy);
    $[97] = effectiveSortBy;
    $[98] = t38;
  } else {
    t38 = $[98];
  }
  const t39 = true;
  let t40;
  if ($[99] !== statusArg) {
    t40 = statusArg !== undefined ? {
      status: statusArg
    } : {};
    $[99] = statusArg;
    $[100] = t40;
  } else {
    t40 = $[100];
  }
  const t41 = categorySelected.length > 0 ? {
    category: categorySelected.map(categoryNameToId).join(",")
  } : {};
  let t42;
  if ($[101] !== debouncedSearch) {
    t42 = debouncedSearch.length > 0 ? {
      search: debouncedSearch
    } : {};
    $[101] = debouncedSearch;
    $[102] = t42;
  } else {
    t42 = $[102];
  }
  let t43;
  if ($[103] !== effectiveSortBy || $[104] !== overhaulArgs || $[105] !== t38 || $[106] !== t40 || $[107] !== t41 || $[108] !== t42 || $[109] !== watchlistFilter) {
    t43 = {
      sortBy: effectiveSortBy,
      sortOrder: t38,
      viewportGated: t39,
      ...t40,
      ...t41,
      ...t42,
      ...overhaulArgs,
      ...watchlistFilter
    };
    $[103] = effectiveSortBy;
    $[104] = overhaulArgs;
    $[105] = t38;
    $[106] = t40;
    $[107] = t41;
    $[108] = t42;
    $[109] = watchlistFilter;
    $[110] = t43;
  } else {
    t43 = $[110];
  }
  let t44;
  if ($[111] !== effectiveSortBy) {
    t44 = effectiveSortBy === "endingSoon" ? {
      refetchInterval: 5000
    } : undefined;
    $[111] = effectiveSortBy;
    $[112] = t44;
  } else {
    t44 = $[112];
  }
  const feedQuery = useGetMarketsInfinite(t43, t44);
  const {
    Items,
    ids,
    isPending,
    isError,
    isPlaceholderData,
    hasNextPage,
    isFetchingNextPage,
    refetch
  } = feedQuery;
  const totalMarkets = ids.length;
  let t45;
  if ($[113] !== ids || $[114] !== isError || $[115] !== isPending || $[116] !== isPlaceholderData) {
    t45 = resolveMarketsFeedPhase({
      isPending,
      isPlaceholderData,
      isError
    }, ids);
    $[113] = ids;
    $[114] = isError;
    $[115] = isPending;
    $[116] = isPlaceholderData;
    $[117] = t45;
  } else {
    t45 = $[117];
  }
  const feedPhase = t45;
  let t46;
  if ($[118] !== refetch) {
    t46 = () => {
      refetch();
    };
    $[118] = refetch;
    $[119] = t46;
  } else {
    t46 = $[119];
  }
  const retryFeed = t46;
  const showCreateCta = totalMarkets >= 1 && !hasNextPage;
  let marketRowIndexById;
  if ($[120] !== ids) {
    marketRowIndexById = new Map();
    for (const id of ids) {
      if (!marketRowIndexById.has(id)) {
        marketRowIndexById.set(id, marketRowIndexById.size);
      }
    }
    $[120] = ids;
    $[121] = marketRowIndexById;
  } else {
    marketRowIndexById = $[121];
  }
  const showFeaturedHero = categorySelected.length === 0 && !watchlistActive;
  let t47;
  if ($[122] === Symbol.for("react.memo_cache_sentinel")) {
    t47 = [];
    $[122] = t47;
  } else {
    t47 = $[122];
  }
  useEffect(_temp6, t47);
  const setActiveMarketsSort = useMarketsSortStore(_temp7);
  let t48;
  let t49;
  if ($[123] !== setActiveMarketsSort || $[124] !== sortBy) {
    t48 = () => {
      setActiveMarketsSort(sortBy);
    };
    t49 = [sortBy, setActiveMarketsSort];
    $[123] = setActiveMarketsSort;
    $[124] = sortBy;
    $[125] = t48;
    $[126] = t49;
  } else {
    t48 = $[125];
    t49 = $[126];
  }
  useEffect(t48, t49);
  let t50;
  if ($[127] !== applyCategoryToUrl || $[128] !== categorySelected || $[129] !== overhaul) {
    t50 = function handleFilterChange(key_0, value_0) {
      if (key_0 === "category") {
        const cats = asStrArr(value_0);
        onEvent("markets_filter_changed", {
          filterType: "category",
          value: cats.length > 0 ? cats.join(",") : "all",
          previousValue: categorySelected.length > 0 ? categorySelected.join(",") : "all"
        });
        applyCategoryToUrl(cats);
        return;
      }
      setOverhaulValue(key_0, value_0);
      const filterType = MARKETS_FILTER_TYPE_BY_KEY[key_0];
      if (filterType !== undefined) {
        onEvent("markets_filter_changed", {
          filterType,
          value: summariseFilterValue(value_0),
          previousValue: summariseFilterValue(overhaul[key_0])
        });
      }
    };
    $[127] = applyCategoryToUrl;
    $[128] = categorySelected;
    $[129] = overhaul;
    $[130] = t50;
  } else {
    t50 = $[130];
  }
  const handleFilterChange = t50;
  let t51;
  if ($[131] !== applyCategoryToUrl || $[132] !== outcomesBounds || $[133] !== setStatus) {
    t51 = function handleClearFilters() {
      setOverhaul(buildEmptyMarketsOverhaul(outcomesBounds));
      applyCategoryToUrl([]);
      setStatus(DEFAULT_STATUS);
    };
    $[131] = applyCategoryToUrl;
    $[132] = outcomesBounds;
    $[133] = setStatus;
    $[134] = t51;
  } else {
    t51 = $[134];
  }
  const handleClearFilters = t51;
  let t52;
  if ($[135] !== setWatchlistActive || $[136] !== watchlistActive) {
    t52 = function handleWatchlistToggle() {
      const next = !watchlistActive;
      setWatchlistActive(next);
      onEvent("markets_watchlist_toggled", {
        active: next
      });
    };
    $[135] = setWatchlistActive;
    $[136] = watchlistActive;
    $[137] = t52;
  } else {
    t52 = $[137];
  }
  const handleWatchlistToggle = t52;
  let t53;
  if ($[138] !== ensureAuthenticated || $[139] !== handleWatchlistToggle) {
    t53 = function handleWatchlistClick() {
      if (!ensureAuthenticated()) {
        return;
      }
      handleWatchlistToggle();
    };
    $[138] = ensureAuthenticated;
    $[139] = handleWatchlistToggle;
    $[140] = t53;
  } else {
    t53 = $[140];
  }
  const handleWatchlistClick = t53;
  let t54;
  if ($[141] !== overhaulArgs) {
    t54 = Object.keys(overhaulArgs);
    $[141] = overhaulArgs;
    $[142] = t54;
  } else {
    t54 = $[142];
  }
  const overhaulActive = t54.length > 0;
  const explicitFilterActive = overhaulActive || categorySelected.length > 0 || debouncedSearch.length > 0 || watchlistActive || status !== DEFAULT_STATUS && status !== "resolved";
  const t55 = status === "resolved";
  const t56 = sortBy === "endingSoon";
  let t57;
  if ($[143] !== explicitFilterActive || $[144] !== t55 || $[145] !== t56) {
    t57 = resolveMarketsEmptyVariant({
      explicitFilterActive,
      isResolvedView: t55,
      isEndingSoonSort: t56
    });
    $[143] = explicitFilterActive;
    $[144] = t55;
    $[145] = t56;
    $[146] = t57;
  } else {
    t57 = $[146];
  }
  const emptyStateVariant = t57;
  const listIsLoading = feedPhase === "loading" || watchlistLoading;
  let t58;
  if ($[147] !== t) {
    t58 = t("sort.trending");
    $[147] = t;
    $[148] = t58;
  } else {
    t58 = $[148];
  }
  let t59;
  if ($[149] !== t58) {
    t59 = {
      label: t58,
      value: "trending",
      prefix: FireIconSolid
    };
    $[149] = t58;
    $[150] = t59;
  } else {
    t59 = $[150];
  }
  let t60;
  if ($[151] !== t) {
    t60 = t("sort.newest");
    $[151] = t;
    $[152] = t60;
  } else {
    t60 = $[152];
  }
  let t61;
  if ($[153] !== t60) {
    t61 = {
      label: t60,
      value: "tradingStartedAt",
      prefix: Plant04IconSolid
    };
    $[153] = t60;
    $[154] = t61;
  } else {
    t61 = $[154];
  }
  let t62;
  if ($[155] !== t) {
    t62 = t("sort.biggestPrize");
    $[155] = t;
    $[156] = t62;
  } else {
    t62 = $[156];
  }
  let t63;
  if ($[157] !== t62) {
    t63 = {
      label: t62,
      value: "prizePool",
      prefix: Award01IconSolid
    };
    $[157] = t62;
    $[158] = t63;
  } else {
    t63 = $[158];
  }
  let t64;
  if ($[159] !== t) {
    t64 = t("sort.endingSoon");
    $[159] = t;
    $[160] = t64;
  } else {
    t64 = $[160];
  }
  let t65;
  if ($[161] !== t64) {
    t65 = {
      label: t64,
      value: "endingSoon",
      prefix: Clock04IconSolid
    };
    $[161] = t64;
    $[162] = t65;
  } else {
    t65 = $[162];
  }
  let t66;
  if ($[163] !== t) {
    t66 = t("sort.resolved");
    $[163] = t;
    $[164] = t66;
  } else {
    t66 = $[164];
  }
  let t67;
  if ($[165] !== t66) {
    t67 = {
      label: t66,
      value: RESOLVED_VIEW_VALUE,
      prefix: CheckmarkCircle02IconSolid
    };
    $[165] = t66;
    $[166] = t67;
  } else {
    t67 = $[166];
  }
  let t68;
  if ($[167] !== t59 || $[168] !== t61 || $[169] !== t63 || $[170] !== t65 || $[171] !== t67) {
    t68 = [t59, t61, t63, t65, t67];
    $[167] = t59;
    $[168] = t61;
    $[169] = t63;
    $[170] = t65;
    $[171] = t67;
    $[172] = t68;
  } else {
    t68 = $[172];
  }
  const sortOptions = t68;
  const activeSortPill = status === "resolved" ? RESOLVED_VIEW_VALUE : sortBy;
  let t69;
  if ($[173] !== setSortBy || $[174] !== setStatus || $[175] !== status) {
    t69 = function handleSortPillChange(next_0) {
      if (next_0 === RESOLVED_VIEW_VALUE) {
        setStatus("resolved");
        return;
      }
      if (status !== DEFAULT_STATUS) {
        setStatus(DEFAULT_STATUS);
      }
      setSortBy(next_0);
    };
    $[173] = setSortBy;
    $[174] = setStatus;
    $[175] = status;
    $[176] = t69;
  } else {
    t69 = $[176];
  }
  const handleSortPillChange = t69;
  let t70;
  if ($[177] !== status) {
    t70 = function handleSortPillEvent(value_1, previous) {
      if (value_1 === RESOLVED_VIEW_VALUE) {
        onEvent("markets_filter_changed", {
          filterType: "status",
          value: "resolved",
          previousValue: status
        });
        return;
      }
      onEvent("markets_sort_changed", {
        sort: value_1,
        previousSort: previous
      });
    };
    $[177] = status;
    $[178] = t70;
  } else {
    t70 = $[178];
  }
  const handleSortPillEvent = t70;
  let t71;
  if ($[179] !== tFilters) {
    t71 = tFilters("any");
    $[179] = tFilters;
    $[180] = t71;
  } else {
    t71 = $[180];
  }
  let t72;
  if ($[181] !== tFilters) {
    t72 = tFilters("after");
    $[181] = tFilters;
    $[182] = t72;
  } else {
    t72 = $[182];
  }
  let t73;
  if ($[183] !== tFilters) {
    t73 = tFilters("before");
    $[183] = tFilters;
    $[184] = t73;
  } else {
    t73 = $[184];
  }
  let t74;
  if ($[185] !== t71 || $[186] !== t72 || $[187] !== t73) {
    t74 = {
      any: t71,
      after: t72,
      before: t73
    };
    $[185] = t71;
    $[186] = t72;
    $[187] = t73;
    $[188] = t74;
  } else {
    t74 = $[188];
  }
  const dateLabels = t74;
  let t75;
  if ($[189] !== tFilters) {
    t75 = tFilters("any");
    $[189] = tFilters;
    $[190] = t75;
  } else {
    t75 = $[190];
  }
  const anyLabel = t75;
  let t76;
  if ($[191] !== regionEnabled || $[192] !== t) {
    t76 = regionEnabled ? [{
      type: "searchMulti" as const,
      key: "location",
      label: t("filter.region"),
      placeholder: t("filter.searchRegions"),
      options: REGION_MULTI_OPTIONS
    }] : [];
    $[191] = regionEnabled;
    $[192] = t;
    $[193] = t76;
  } else {
    t76 = $[193];
  }
  let t77;
  if ($[194] !== t) {
    t77 = t("filter.creator");
    $[194] = t;
    $[195] = t77;
  } else {
    t77 = $[195];
  }
  let t78;
  if ($[196] !== t) {
    t78 = t("filter.searchCreators");
    $[196] = t;
    $[197] = t78;
  } else {
    t78 = $[197];
  }
  let t79;
  if ($[198] !== t) {
    t79 = t("filter.noCreators");
    $[198] = t;
    $[199] = t79;
  } else {
    t79 = $[199];
  }
  let t80;
  if ($[200] !== t77 || $[201] !== t78 || $[202] !== t79) {
    t80 = {
      type: "creatorMulti",
      key: "creator",
      label: t77,
      placeholder: t78,
      emptyLabel: t79
    };
    $[200] = t77;
    $[201] = t78;
    $[202] = t79;
    $[203] = t80;
  } else {
    t80 = $[203];
  }
  let t81;
  if ($[204] !== t) {
    t81 = t("filter.category");
    $[204] = t;
    $[205] = t81;
  } else {
    t81 = $[205];
  }
  let t82;
  if ($[206] !== t81) {
    t82 = {
      type: "chipsMulti",
      key: "category",
      label: t81,
      options: CATEGORY_CHIP_OPTIONS
    };
    $[206] = t81;
    $[207] = t82;
  } else {
    t82 = $[207];
  }
  let t83;
  if ($[208] !== t) {
    t83 = t("filter.numberOfOutcomes");
    $[208] = t;
    $[209] = t83;
  } else {
    t83 = $[209];
  }
  const t84 = outcomesBounds.min;
  const t85 = outcomesBounds.max;
  let t86;
  if ($[210] !== t) {
    t86 = t("filter.outcomesUnit");
    $[210] = t;
    $[211] = t86;
  } else {
    t86 = $[211];
  }
  let t87;
  if ($[212] !== anyLabel || $[213] !== t86) {
    t87 = {
      format: "count",
      unit: t86,
      anyLabel
    };
    $[212] = anyLabel;
    $[213] = t86;
    $[214] = t87;
  } else {
    t87 = $[214];
  }
  let t88;
  if ($[215] !== outcomesBounds.max || $[216] !== outcomesBounds.min || $[217] !== t83 || $[218] !== t87) {
    t88 = {
      type: "slider",
      key: "outcomes",
      label: t83,
      min: t84,
      max: t85,
      step: 1,
      format: t87
    };
    $[215] = outcomesBounds.max;
    $[216] = outcomesBounds.min;
    $[217] = t83;
    $[218] = t87;
    $[219] = t88;
  } else {
    t88 = $[219];
  }
  let t89;
  if ($[220] !== t) {
    t89 = t("filter.totalVolume");
    $[220] = t;
    $[221] = t89;
  } else {
    t89 = $[221];
  }
  let t90;
  if ($[222] !== anyLabel || $[223] !== currencySymbol || $[224] !== exchangeRate) {
    t90 = {
      format: "money",
      anyLabel,
      rate: exchangeRate,
      symbol: currencySymbol
    };
    $[222] = anyLabel;
    $[223] = currencySymbol;
    $[224] = exchangeRate;
    $[225] = t90;
  } else {
    t90 = $[225];
  }
  let t91;
  if ($[226] !== t89 || $[227] !== t90) {
    t91 = {
      type: "slider",
      key: "volume",
      label: t89,
      min: M.volume.min,
      max: M.volume.max,
      format: t90
    };
    $[226] = t89;
    $[227] = t90;
    $[228] = t91;
  } else {
    t91 = $[228];
  }
  let t92;
  if ($[229] !== t) {
    t92 = t("filter.prizePool");
    $[229] = t;
    $[230] = t92;
  } else {
    t92 = $[230];
  }
  let t93;
  if ($[231] !== anyLabel || $[232] !== currencySymbol || $[233] !== exchangeRate) {
    t93 = {
      format: "money",
      anyLabel,
      rate: exchangeRate,
      symbol: currencySymbol
    };
    $[231] = anyLabel;
    $[232] = currencySymbol;
    $[233] = exchangeRate;
    $[234] = t93;
  } else {
    t93 = $[234];
  }
  let t94;
  if ($[235] !== t92 || $[236] !== t93) {
    t94 = {
      type: "slider",
      key: "prizePool",
      label: t92,
      min: M.prizePool.min,
      max: M.prizePool.max,
      format: t93
    };
    $[235] = t92;
    $[236] = t93;
    $[237] = t94;
  } else {
    t94 = $[237];
  }
  let t95;
  if ($[238] !== t) {
    t95 = t("filter.liquidity");
    $[238] = t;
    $[239] = t95;
  } else {
    t95 = $[239];
  }
  let t96;
  if ($[240] !== anyLabel || $[241] !== currencySymbol || $[242] !== exchangeRate) {
    t96 = {
      format: "money",
      anyLabel,
      rate: exchangeRate,
      symbol: currencySymbol
    };
    $[240] = anyLabel;
    $[241] = currencySymbol;
    $[242] = exchangeRate;
    $[243] = t96;
  } else {
    t96 = $[243];
  }
  let t97;
  if ($[244] !== t95 || $[245] !== t96) {
    t97 = {
      type: "slider",
      key: "liquidity",
      label: t95,
      min: M.liquidity.min,
      max: M.liquidity.max,
      format: t96
    };
    $[244] = t95;
    $[245] = t96;
    $[246] = t97;
  } else {
    t97 = $[246];
  }
  let t98;
  if ($[247] !== t) {
    t98 = t("filter.backers");
    $[247] = t;
    $[248] = t98;
  } else {
    t98 = $[248];
  }
  let t99;
  if ($[249] !== anyLabel) {
    t99 = {
      format: "int",
      anyLabel
    };
    $[249] = anyLabel;
    $[250] = t99;
  } else {
    t99 = $[250];
  }
  let t100;
  if ($[251] !== t98 || $[252] !== t99) {
    t100 = {
      type: "slider",
      key: "backers",
      label: t98,
      min: M.backers.min,
      max: M.backers.max,
      format: t99
    };
    $[251] = t98;
    $[252] = t99;
    $[253] = t100;
  } else {
    t100 = $[253];
  }
  let t101;
  if ($[254] !== t) {
    t101 = t("filter.traders");
    $[254] = t;
    $[255] = t101;
  } else {
    t101 = $[255];
  }
  let t102;
  if ($[256] !== anyLabel) {
    t102 = {
      format: "int",
      anyLabel
    };
    $[256] = anyLabel;
    $[257] = t102;
  } else {
    t102 = $[257];
  }
  let t103;
  if ($[258] !== t101 || $[259] !== t102) {
    t103 = {
      type: "slider",
      key: "traders",
      label: t101,
      min: M.traders.min,
      max: M.traders.max,
      format: t102
    };
    $[258] = t101;
    $[259] = t102;
    $[260] = t103;
  } else {
    t103 = $[260];
  }
  let t104;
  if ($[261] !== t) {
    t104 = t("filter.createdAt");
    $[261] = t;
    $[262] = t104;
  } else {
    t104 = $[262];
  }
  let t105;
  if ($[263] !== dateLabels || $[264] !== t104) {
    t105 = {
      type: "dateSlider",
      key: "createdAt",
      label: t104,
      spanDays: MARKETS_CREATED_SPAN_DAYS,
      labels: dateLabels
    };
    $[263] = dateLabels;
    $[264] = t104;
    $[265] = t105;
  } else {
    t105 = $[265];
  }
  let t106;
  if ($[266] !== marketTypeFilter || $[267] !== t100 || $[268] !== t103 || $[269] !== t105 || $[270] !== t76 || $[271] !== t80 || $[272] !== t82 || $[273] !== t88 || $[274] !== t91 || $[275] !== t94 || $[276] !== t97) {
    t106 = [...marketTypeFilter, ...t76, t80, t82, t88, t91, t94, t97, t100, t103, t105];
    $[266] = marketTypeFilter;
    $[267] = t100;
    $[268] = t103;
    $[269] = t105;
    $[270] = t76;
    $[271] = t80;
    $[272] = t82;
    $[273] = t88;
    $[274] = t91;
    $[275] = t94;
    $[276] = t97;
    $[277] = t106;
  } else {
    t106 = $[277];
  }
  const filterDimensions = t106;
  let t107;
  if ($[278] !== regionEnabled) {
    t107 = regionEnabled ? {} : {
      location: []
    };
    $[278] = regionEnabled;
    $[279] = t107;
  } else {
    t107 = $[279];
  }
  let t108;
  if ($[280] !== categorySelected || $[281] !== overhaul || $[282] !== t107) {
    t108 = {
      ...overhaul,
      ...t107,
      category: categorySelected
    };
    $[280] = categorySelected;
    $[281] = overhaul;
    $[282] = t107;
    $[283] = t108;
  } else {
    t108 = $[283];
  }
  const activeFilterValues = t108;
  let t109;
  if ($[284] !== setFilterOpen) {
    t109 = () => setFilterOpen(true);
    $[284] = setFilterOpen;
    $[285] = t109;
  } else {
    t109 = $[285];
  }
  let t110;
  if ($[286] !== filterOpen || $[287] !== handleWatchlistClick || $[288] !== t109 || $[289] !== watchlistActive) {
    t110 = <MarketsDiscoveryActions filterOpen={filterOpen} onFilterClick={t109} watchlistActive={watchlistActive} onWatchlistClick={handleWatchlistClick} />;
    $[286] = filterOpen;
    $[287] = handleWatchlistClick;
    $[288] = t109;
    $[289] = watchlistActive;
    $[290] = t110;
  } else {
    t110 = $[290];
  }
  const headerActions = t110;
  let t111;
  if ($[291] !== marketsWatchlist?.pubkeys) {
    t111 = marketsWatchlist?.pubkeys ?? [];
    $[291] = marketsWatchlist?.pubkeys;
    $[292] = t111;
  } else {
    t111 = $[292];
  }
  let t112;
  if ($[293] !== Items || $[294] !== activeFilterValues || $[295] !== activeSortPill || $[296] !== category || $[297] !== emptyStateVariant || $[298] !== feedPhase || $[299] !== filterDimensions || $[300] !== filterOpen || $[301] !== handleClearFilters || $[302] !== handleFilterChange || $[303] !== handleSortPillChange || $[304] !== handleSortPillEvent || $[305] !== hasNextPage || $[306] !== headerActions || $[307] !== ids || $[308] !== isFetchingNextPage || $[309] !== listIsLoading || $[310] !== marketRowIndexById || $[311] !== retryFeed || $[312] !== setFilterOpen || $[313] !== showCreateCta || $[314] !== showFeaturedHero || $[315] !== sortBy || $[316] !== sortOptions || $[317] !== status || $[318] !== t || $[319] !== t111 || $[320] !== tFilters || $[321] !== totalMarkets) {
    t112 = {
      t,
      tFilters,
      category,
      status,
      sortBy,
      showFeaturedHero,
      marketWatchlistPubkeys: t111,
      feedPhase,
      retryFeed,
      listIsLoading,
      totalMarkets,
      emptyStateVariant,
      showCreateCta,
      hasNextPage,
      isFetchingNextPage,
      ids,
      Items,
      marketRowIndexById,
      sortOptions,
      activeSortPill,
      handleSortPillChange,
      handleSortPillEvent,
      filterDimensions,
      activeFilterValues,
      handleFilterChange,
      handleClearFilters,
      filterOpen,
      setFilterOpen,
      headerActions
    };
    $[293] = Items;
    $[294] = activeFilterValues;
    $[295] = activeSortPill;
    $[296] = category;
    $[297] = emptyStateVariant;
    $[298] = feedPhase;
    $[299] = filterDimensions;
    $[300] = filterOpen;
    $[301] = handleClearFilters;
    $[302] = handleFilterChange;
    $[303] = handleSortPillChange;
    $[304] = handleSortPillEvent;
    $[305] = hasNextPage;
    $[306] = headerActions;
    $[307] = ids;
    $[308] = isFetchingNextPage;
    $[309] = listIsLoading;
    $[310] = marketRowIndexById;
    $[311] = retryFeed;
    $[312] = setFilterOpen;
    $[313] = showCreateCta;
    $[314] = showFeaturedHero;
    $[315] = sortBy;
    $[316] = sortOptions;
    $[317] = status;
    $[318] = t;
    $[319] = t111;
    $[320] = tFilters;
    $[321] = totalMarkets;
    $[322] = t112;
  } else {
    t112 = $[322];
  }
  return t112;
}
function _temp7(s) {
  return s.setActiveSort;
}
function _temp6() {
  onEvent("markets_page_viewed", {
    path: "/markets",
    locale: "en"
  });
}
function _temp5(o_0) {
  return locationNameToId(o_0.value);
}
function _temp4() {
  return Date.now();
}
function _temp3(c) {
  return c !== null;
}
function _temp2(config_0) {
  return config_0.maxOutcomes;
}
function _temp(config) {
  return config.minOutcomes;
}
export function MarketsPage() {
  const $ = _c(64);
  const stackedMarketCards = useFeatureGate("stacked_market_cards") === true;
  const desktopCards = useMediaQuery("(min-width: 768px)");
  const {
    t,
    tFilters,
    category,
    status,
    sortBy,
    showFeaturedHero,
    marketWatchlistPubkeys,
    feedPhase,
    retryFeed,
    listIsLoading,
    totalMarkets,
    emptyStateVariant,
    showCreateCta,
    hasNextPage,
    isFetchingNextPage,
    ids,
    Items,
    marketRowIndexById,
    sortOptions,
    activeSortPill,
    handleSortPillChange,
    handleSortPillEvent,
    filterDimensions,
    activeFilterValues,
    handleFilterChange,
    handleClearFilters,
    filterOpen,
    setFilterOpen,
    headerActions
  } = useMarketsPage();
  const t0 = feedPhase !== "loading";
  let t1;
  if ($[0] !== t0) {
    t1 = {
      name: "markets",
      isReady: t0
    };
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  useRoutePerf(t1);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = cn("w-full", LISTING_INDEX_PAGE_STACK_CLASS);
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== marketWatchlistPubkeys || $[4] !== showFeaturedHero) {
    t3 = <MarketsFeaturedSection visible={showFeaturedHero} watchlistPubkeys={marketWatchlistPubkeys} />;
    $[3] = marketWatchlistPubkeys;
    $[4] = showFeaturedHero;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== category || $[7] !== headerActions) {
    t4 = <div id="all-markets" className="scroll-mt-35"><MarketsHeader category={category} actions={headerActions} /></div>;
    $[6] = category;
    $[7] = headerActions;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== tFilters) {
    t5 = tFilters("sheetTitleMarkets");
    $[9] = tFilters;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== tFilters) {
    t6 = tFilters("clearAll");
    $[11] = tFilters;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t) {
    t7 = t("filter.all");
    $[13] = t;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== tFilters) {
    t8 = tFilters("close");
    $[15] = tFilters;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== tFilters) {
    t9 = tFilters("reset");
    $[17] = tFilters;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== tFilters) {
    t10 = tFilters("showResults");
    $[19] = tFilters;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== tFilters) {
    t11 = count => tFilters("nSelected", {
      count
    });
    $[21] = tFilters;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== activeFilterValues || $[24] !== activeSortPill || $[25] !== filterDimensions || $[26] !== filterOpen || $[27] !== handleClearFilters || $[28] !== handleFilterChange || $[29] !== handleSortPillChange || $[30] !== handleSortPillEvent || $[31] !== setFilterOpen || $[32] !== sortOptions || $[33] !== t10 || $[34] !== t11 || $[35] !== t5 || $[36] !== t6 || $[37] !== t7 || $[38] !== t8 || $[39] !== t9) {
    t12 = <div className="mt-4 flex items-start gap-2 md:mt-6"><div className="flex-1 min-w-0"><SortFilterBar sortOptions={sortOptions} activeSort={activeSortPill} onSortChange={handleSortPillChange} filters={filterDimensions} activeFilters={activeFilterValues} onFilterChange={handleFilterChange} onClearAll={handleClearFilters} filterSheetTitle={t5} clearAllLabel={t6} filterButtonLabel={t7} closeLabel={t8} resetLabel={t9} applyLabel={t10} selectedCountLabel={t11} onSortEvent={handleSortPillEvent} filterOpen={filterOpen} onFilterOpenChange={setFilterOpen} hideInlineSettings={true} /></div></div>;
    $[23] = activeFilterValues;
    $[24] = activeSortPill;
    $[25] = filterDimensions;
    $[26] = filterOpen;
    $[27] = handleClearFilters;
    $[28] = handleFilterChange;
    $[29] = handleSortPillChange;
    $[30] = handleSortPillEvent;
    $[31] = setFilterOpen;
    $[32] = sortOptions;
    $[33] = t10;
    $[34] = t11;
    $[35] = t5;
    $[36] = t6;
    $[37] = t7;
    $[38] = t8;
    $[39] = t9;
    $[40] = t12;
  } else {
    t12 = $[40];
  }
  let t13;
  if ($[41] !== t12 || $[42] !== t4) {
    t13 = <div className="flex flex-col">{t4}{t12}</div>;
    $[41] = t12;
    $[42] = t4;
    $[43] = t13;
  } else {
    t13 = $[43];
  }
  let t14;
  if ($[44] !== Items || $[45] !== desktopCards || $[46] !== emptyStateVariant || $[47] !== feedPhase || $[48] !== hasNextPage || $[49] !== ids || $[50] !== isFetchingNextPage || $[51] !== listIsLoading || $[52] !== marketRowIndexById || $[53] !== retryFeed || $[54] !== showCreateCta || $[55] !== sortBy || $[56] !== stackedMarketCards || $[57] !== status || $[58] !== totalMarkets) {
    t14 = feedPhase === "initialError" ? <MarketsFeedError phase="initial" onRetry={retryFeed} /> : listIsLoading ? <div data-testid="markets-grid-loading" className="grid grid-flow-row-dense grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">{SKELETON_STRIPE_KEYS.map(_temp8)}</div> : totalMarkets === 0 ? emptyStateVariant === "filtered" ? <NoResults /> : <EmptyState variant={emptyStateVariant} /> : <>{feedPhase === "staleError" ? <MarketsFeedError phase="stale" onRetry={retryFeed} /> : null}<MarketFeedTradeProvider marketIds={ids}><div data-testid="loaded-markets-grid" className={cn("grid grid-flow-row-dense grid-cols-1 md:grid-cols-2 lg:grid-cols-3 md:gap-4", stackedMarketCards ? "gap-0" : "gap-3")}>{status === DEFAULT_STATUS && <IntroWidget active={sortBy === DEFAULT_SORT} />}<Items>{t15 => {
              const {
                ref,
                id
              } = t15;
              return <MarketRowCard ref={ref} pubkey={id} outcomeLayout={stackedMarketCards ? "stacked" : "bars"} surface={stackedMarketCards && !desktopCards ? "list" : "card"} listDivider={(marketRowIndexById.get(id) ?? 0) < ids.length - 1} priorityImage={(marketRowIndexById.get(id) ?? 0) < 6} />;
            }}</Items>{showCreateCta ? <SparseCard outcomeLayout={stackedMarketCards ? "stacked" : "bars"} surface={stackedMarketCards && !desktopCards ? "list" : "card"} /> : null}</div></MarketFeedTradeProvider>{hasNextPage && isFetchingNextPage && <div className="flex justify-center py-4"><Icon icon={Loading03Icon} className="size-6 animate-spin text-foreground-secondary" /></div>}</>;
    $[44] = Items;
    $[45] = desktopCards;
    $[46] = emptyStateVariant;
    $[47] = feedPhase;
    $[48] = hasNextPage;
    $[49] = ids;
    $[50] = isFetchingNextPage;
    $[51] = listIsLoading;
    $[52] = marketRowIndexById;
    $[53] = retryFeed;
    $[54] = showCreateCta;
    $[55] = sortBy;
    $[56] = stackedMarketCards;
    $[57] = status;
    $[58] = totalMarkets;
    $[59] = t14;
  } else {
    t14 = $[59];
  }
  let t15;
  if ($[60] !== t13 || $[61] !== t14 || $[62] !== t3) {
    t15 = <div className={t2}>{t3}{t13}{t14}</div>;
    $[60] = t13;
    $[61] = t14;
    $[62] = t3;
    $[63] = t15;
  } else {
    t15 = $[63];
  }
  return t15;
}
function _temp8(key) {
  return <MarketRowCardSkeleton key={key} />;
}